import React, {Fragment, lazy, Suspense} from "react";
import {Redirect, Route, Switch} from "react-router-dom";
import DashboardLayout from "src/layouts/DashboardLayout";
import LoadingScreen from "src/components/LoadingScreen";
import AuthGuard from "src/guards/AuthGuard";
import GuestGuard from "src/guards/GuestGuard";
import AdminGuard from "src/guards/AdminGuard";
import UserGuard from "src/guards/UserGuard";
import TokenGuard from "src/guards/TokenGuard";
import {URL_TEMPLATES as URL} from "src/urlTemplates";

export const renderRoutes = (routes = []) => (
    <Suspense fallback={<LoadingScreen/>}>
        <Switch>
            {routes.map((route, i) => {
                const Guard = route.guard || Fragment;
                const Layout = route.layout || Fragment;
                const Component = route.component;

                return (
                    <Route
                        key={i}
                        path={route.path}
                        exact={route.exact}
                        render={props => (
                            <Guard>
                                <Layout>{route.routes ? renderRoutes(route.routes) : <Component {...props} {...route.props} />}</Layout>
                            </Guard>
                        )}
                    />
                );
            })}
        </Switch>
    </Suspense>
);

const routes = [
    {
        exact: true,
        path: URL._404,
        component: lazy(() => import("src/views/errors/NotFoundView"))
    },
    {
        exact: true,
        path: URL.SSO,
        guard: TokenGuard,
        component: lazy(() => import("src/views/auth/SsoLoginView"))
    },
    {
        exact: true,
        path: URL.LOGIN,
        guard: GuestGuard,
        component: lazy(() => import("src/views/auth/LoginView"))
    },
    {
        exact: true,
        path: URL.PASSWORD_FORGOT,
        guard: GuestGuard,
        component: lazy(() => import("src/views/auth/PasswordForgotView"))
    },
    {
        exact: true,
        path: URL.PASSWORD_RESET,
        guard: TokenGuard,
        component: lazy(() => import("src/views/auth/PasswordResetView"))
    },
    {
        path: URL.APP,
        guard: AuthGuard,
        layout: DashboardLayout,
        routes: [
            {
                exact: true,
                path: URL.APP,
                component: () => <Redirect to={URL.ORDER}/>
            },
            {
                exact: true,
                path: URL.ACCOUNT,
                component: lazy(() => import("src/views/account/AccountView"))
            },
            {
                path: URL.USER,
                routes: [
                    {
                        exact: true,
                        path: URL.EDIT_USER,
                        guard: AdminGuard,
                        component: lazy(() => import("src/views/management/User/EditUserView"))
                    },
                    {
                        component: () => <Redirect to={URL._404}/>
                    }
                ]
            },
            {
                path: URL.ORDER,
                routes: [
                    {
                        exact: true,
                        path: URL.ORDER,
                        guard: UserGuard,
                        component: lazy(() => import("src/views/order/OrderListView"))
                    },
                    {
                        exact: true,
                        path: URL.ORDER_DETAIL,
                        guard: UserGuard,
                        component: lazy(() => import("src/views/order/OrderDetailsView"))
                    },
                    {
                        exact: true,
                        path: URL.REQUEST_CERTIFICATE,
                        guard: UserGuard,
                        component: lazy(() => import("src/views/order/RequestCertificateView"))
                    },
                    {
                        exact: true,
                        path: URL.REISSUE_CERTIFICATE,
                        guard: UserGuard,
                        component: lazy(() => import("src/views/order/ReissueCertificateView"))
                    },
                    {
                        exact: true,
                        path: URL.RENEW_CERTIFICATE,
                        guard: UserGuard,
                        component: lazy(() => import("src/views/order/RenewCertificateView"))
                    },
                    {
                        exact: true,
                        path: URL.EDIT_ORDER_TENANT,
                        guard: AdminGuard,
                        component: lazy(() => import("src/views/order/EditOrderTenantView"))
                    },
                    {
                        component: () => <Redirect to={URL._404}/>
                    }
                ]
            },
            {
                path: URL.TENANT,
                routes: [
                    {
                        exact: true,
                        guard: AdminGuard,
                        path: [URL.TENANT, URL.TENANT_LIST],
                        component: lazy(() => import("src/views/management/Tenant/TenantListView"))
                    },
                    {
                        exact: true,
                        path: URL.CREATE_TENANT,
                        guard: AdminGuard,
                        component: lazy(() => import("src/views/management/Tenant/CreateTenantView"))
                    },
                    {
                        exact: true,
                        path: URL.EDIT_TENANT,
                        guard: AdminGuard,
                        component: lazy(() => import("src/views/management/Tenant/EditTenantView"))
                    },
                    {
                        exact: true,
                        path: URL.USER_LIST,
                        guard: AdminGuard,
                        component: lazy(() => import("src/views/management/User/UserListView"))
                    },
                    {
                        exact: true,
                        path: URL.CREATE_USER,
                        guard: AdminGuard,
                        component: lazy(() => import("src/views/management/User/CreateUserView"))
                    },
                    {
                        component: () => <Redirect to={URL._404}/>
                    }
                ]
            },
            {
                path: URL.ORGANISATION,
                routes: [
                    {
                        exact: true,
                        path: URL.ORGANISATION,
                        guard: UserGuard,
                        component: lazy(() => import("src/views/organisation/OrganisationListView"))
                    },
                    {
                        exact: true,
                        path: URL.ORGANISATION_DETAIL,
                        guard: UserGuard,
                        component: lazy(() => import("src/views/organisation/OrganisationDetailsView"))
                    },
                    {
                        exact: true,
                        path: URL.CREATE_ORGANISATION,
                        guard: UserGuard,
                        component: lazy(() => import("src/views/organisation/CreateOrganisationView"))
                    },
                    {
                        exact: true,
                        path: URL.RENEW_ORGANISATION,
                        guard: UserGuard,
                        component: lazy(() => import("src/views/organisation/EditOrganisationView"))
                    }
                ]
            },
            {
                component: () => <Redirect to={URL._404}/>
            }
        ]
    },
    {
        path: "*",
        routes: [
            {
                exact: true,
                path: "/",
                component: () => <Redirect to="/app"/>
            },
            {
                component: () => <Redirect to={URL._404}/>
            }
        ]
    }
];

export default routes;
